import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DeleteOutline, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

import { bulkDeletePromoCodesByIds } from '~/services/PromoService';
import type { GetPromoCodeProps } from '~/services/PromoService';

import { chunkArray } from '~/utils/chunkArray';

export type BulkOperation = 'delete';
export type BulkOperationRowStatus = 'pending' | 'deleted' | 'failed';
type DeleteScope = 'page' | 'search';
type PromoCodeId = Pick<App.PromoData, 'id_promo_code'>;

interface Props {
  currentPagePromos: Array<PromoCodeId>;
  totalSearchResults: number;
  isUnfilteredAllSearch: boolean;
  searchFilters: GetPromoCodeProps;
  /** Fetches one stable page of the current search without changing the visible results. */
  loadSearchPage: (page: number, batchSize: number) => Promise<Array<App.PromoData>>;
  onComplete: () => Promise<void>;
  onOperationChange: (operation: BulkOperation | null) => void;
  onStatusChange: (statuses: Record<string, BulkOperationRowStatus>) => void;
}

type DeleteProgress = {
  deleted: number;
  estimatedFinishAt?: number;
  failed: number;
  isRequestInProgress: boolean;
  processed: number;
  total: number;
  batch: number;
  totalBatches: number;
  stopped?: boolean;
};

type DeleteCompletion = {
  deleted: number;
  failed: number;
  requested: number;
  stopped: boolean;
};

type DeletePreview = {
  promoCodeIds: Array<string>;
  notFoundPromoCodeIds: Array<string>;
  requested: number;
  batches: number;
};

type BatchLoadProgress = {
  loaded: number;
  total: number;
  page: number;
};

export const PROMO_DELETE_BATCH_SIZE = 25;
export const DEFAULT_PROMO_ID_LOAD_BATCH_SIZE = 500;
export const AUTO_LOAD_PROMO_ID_LIMIT = 100;

export function shouldLoadSearchPromoIdsImmediately(totalSearchResults: number) {
  return totalSearchResults > 0 && totalSearchResults < AUTO_LOAD_PROMO_ID_LIMIT;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

type PromoDeleteResult = {
  promoCodeIds: Array<string>;
  notFoundPromoCodeIds: Array<string>;
};

export function getPromoDeleteResult(
  response: unknown,
  requestedPromoCodeIds: Array<string>,
  expectedDryRun: boolean,
): PromoDeleteResult {
  if (!isRecord(response) || !isRecord(response.result)) {
    throw new Error('Promo deletion returned an invalid success response.');
  }

  if (response.result.dryRun !== expectedDryRun) {
    throw new Error('Promo deletion returned a response for the wrong operation.');
  }

  const promoCodeIds = response.result.promoCodeIds;
  const notFoundPromoCodeIds = response.result.notFoundPromoCodeIds;
  if (
    !Array.isArray(promoCodeIds) ||
    !Array.isArray(notFoundPromoCodeIds) ||
    !promoCodeIds.every((promoCodeId) => typeof promoCodeId === 'string') ||
    !notFoundPromoCodeIds.every((promoCodeId) => typeof promoCodeId === 'string')
  ) {
    throw new Error('Promo deletion completed without confirming the deleted promo code IDs.');
  }

  const requestedIds = new Set(requestedPromoCodeIds);
  const confirmedIds = new Set(promoCodeIds);
  const notFoundIds = new Set(notFoundPromoCodeIds);
  const accountedForIds = new Set([...confirmedIds, ...notFoundIds]);

  if (
    requestedIds.size !== requestedPromoCodeIds.length ||
    confirmedIds.size !== promoCodeIds.length ||
    notFoundIds.size !== notFoundPromoCodeIds.length ||
    [...confirmedIds].some((promoCodeId) => !requestedIds.has(promoCodeId)) ||
    [...notFoundIds].some((promoCodeId) => !requestedIds.has(promoCodeId)) ||
    [...confirmedIds].some((promoCodeId) => notFoundIds.has(promoCodeId)) ||
    accountedForIds.size !== requestedIds.size
  ) {
    throw new Error('Promo deletion did not account for exactly the reviewed promo code IDs.');
  }

  return { promoCodeIds: [...confirmedIds], notFoundPromoCodeIds: [...notFoundIds] };
}

export function getAppliedPromoSearchFilters(filters: GetPromoCodeProps): Array<string> {
  return [
    ...(filters.codeName && filters.codeName.toUpperCase() !== 'ALL' ? [`Promo Code Name: ${filters.codeName}`] : []),
    ...(filters.emailAddress ? [`Promo Email Restriction: ${filters.emailAddress}`] : []),
    ...(filters.caseId ? [`Case ID: ${filters.caseId}`] : []),
    ...(filters.userIdInteracted ? [`Filter by User ID Interacted: ${filters.userIdInteracted}`] : []),
    ...(filters.deptTag ? [`Department: ${filters.deptTag}`] : []),
    ...(filters.categoryTag ? [`Category: ${filters.categoryTag}`] : []),
    ...(filters.subCategoryTag ? [`Sub Category: ${filters.subCategoryTag}`] : []),
    ...(filters.expiresAfter ? ['Hide Expired'] : []),
    ...(filters.noChildPromos ? ['Hide Child Promos'] : []),
    ...(filters.onlyChildPromos ? ['Child Promos Only'] : []),
    ...(filters.excludeReferralPromos ? ['Exclude Referral Promo'] : []),
    ...(filters.excludeApprovedPromos ? ['Hide Approved'] : []),
    ...(filters.dev ? ['Is Dev'] : []),
  ];
}

export function getSearchPageCount(totalSearchResults: number, pageSize: number) {
  return Math.ceil(totalSearchResults / Math.max(pageSize, 1));
}

export async function loadAllSearchPromoIds(
  totalSearchResults: number,
  batchSize: number,
  loadSearchPage: (page: number, batchSize: number) => Promise<Array<PromoCodeId>>,
  onProgress: (progress: BatchLoadProgress) => void,
  shouldCancel: () => boolean = () => false,
) {
  const promoCodeIds = new Set<string>();
  const pageCount = getSearchPageCount(totalSearchResults, batchSize);

  for (let page = 1; page <= pageCount; page += 1) {
    if (shouldCancel()) {
      return null;
    }
    const promos = await loadSearchPage(page, batchSize);
    if (shouldCancel()) {
      return null;
    }
    promos.forEach((promo) => promoCodeIds.add(promo.id_promo_code));
    onProgress({ loaded: promoCodeIds.size, total: totalSearchResults, page });
  }

  if (promoCodeIds.size !== totalSearchResults) {
    throw new Error(
      `Loaded ${promoCodeIds.size} of ${totalSearchResults} promo codes. Search results changed; refresh the search before loading again.`,
    );
  }

  return Array.from(promoCodeIds);
}

function PromoBulkOperations({
  currentPagePromos,
  totalSearchResults,
  isUnfilteredAllSearch,
  searchFilters,
  loadSearchPage,
  onComplete,
  onOperationChange,
  onStatusChange,
}: Props) {
  const [isBulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [deleteScope, setDeleteScope] = useState<DeleteScope>('page');
  const [isLoadingSearchBatch, setLoadingSearchBatch] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [loadedSearchPromoIds, setLoadedSearchPromoIds] = useState<Array<string> | null>(null);
  const [loadedSearchFilters, setLoadedSearchFilters] = useState<Array<string> | null>(null);
  const [batchLoadProgress, setBatchLoadProgress] = useState<BatchLoadProgress | null>(null);
  const [preview, setPreview] = useState<DeletePreview | null>(null);
  const [isPreviewing, setPreviewing] = useState(false);
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [progress, setProgress] = useState<DeleteProgress | null>(null);
  const [completion, setCompletion] = useState<DeleteCompletion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stopRequestedRef = useRef(false);
  const cancelLoadRequestedRef = useRef(false);

  useEffect(() => {
    setLoadedSearchPromoIds(null);
    setLoadedSearchFilters(null);
    setBatchLoadProgress(null);
    setPreview(null);
    setDeleteConfirmationOpen(false);
    setCompletion(null);
    setError(null);
  }, [loadSearchPage]);

  const setBulkDeleteModeAndReset = (enabled: boolean) => {
    if (enabled && isUnfilteredAllSearch) {
      return;
    }
    if (enabled) {
      cancelLoadRequestedRef.current = true;
      setLoadedSearchPromoIds(null);
      setLoadedSearchFilters(null);
      setBatchLoadProgress(null);
      setPreview(null);
      setDeleteConfirmationOpen(false);
      setCompletion(null);
      setError(null);
    }
    setBulkDeleteMode(enabled);
    onOperationChange(enabled ? 'delete' : null);
    onStatusChange({});
    setProgress(null);
    setPreview(null);
    setDeleteConfirmationOpen(false);
    setCompletion(null);
    setError(null);
  };

  const loadSearchPromoIds = useCallback(async () => {
    cancelLoadRequestedRef.current = false;
    setLoadingSearchBatch(true);
    setLoadedSearchPromoIds(null);
    setLoadedSearchFilters(null);
    setBatchLoadProgress(null);
    setPreview(null);
    setError(null);

    try {
      const promoCodeIds = await loadAllSearchPromoIds(
        totalSearchResults,
        DEFAULT_PROMO_ID_LOAD_BATCH_SIZE,
        loadSearchPage,
        setBatchLoadProgress,
        () => cancelLoadRequestedRef.current,
      );
      if (promoCodeIds) {
        setLoadedSearchPromoIds(promoCodeIds);
        setLoadedSearchFilters(getAppliedPromoSearchFilters(searchFilters));
        return promoCodeIds;
      }
    } catch (loadError) {
      setError((loadError as Error).message || 'Unable to load the matching promo codes.');
    } finally {
      setLoadingSearchBatch(false);
    }
    return null;
  }, [loadSearchPage, searchFilters, totalSearchResults]);

  const changeDeleteScope = (scope: DeleteScope) => {
    setDeleteScope(scope);
    onStatusChange({});
    setProgress(null);
    setPreview(null);
    setDeleteConfirmationOpen(false);
    setCompletion(null);
    setError(null);

    if (scope === 'search' && shouldLoadSearchPromoIdsImmediately(totalSearchResults)) {
      void loadSearchPromoIds();
    }
  };

  const cancelSearchBatchLoad = () => {
    cancelLoadRequestedRef.current = true;
    setLoadedSearchPromoIds(null);
    setLoadedSearchFilters(null);
    setBatchLoadProgress(null);
  };

  const deletePromoIds = useCallback(
    async (promoCodeIds: Array<string>) => {
      const visiblePromoIds = new Set(currentPagePromos.map((promo) => promo.id_promo_code));
      const visibleIdsInBatch = promoCodeIds.filter((id) => visiblePromoIds.has(id));
      if (visibleIdsInBatch.length > 0) {
        onStatusChange(Object.fromEntries(visibleIdsInBatch.map((id) => [id, 'pending' as const])));
      }

      try {
        const response = await bulkDeletePromoCodesByIds({ promoCodeIds, dryRun: false });
        const { promoCodeIds: confirmedPromoCodeIds } = getPromoDeleteResult(response, promoCodeIds, false);
        const deletedIds = new Set(confirmedPromoCodeIds);
        if (visibleIdsInBatch.length > 0) {
          onStatusChange(
            Object.fromEntries(visibleIdsInBatch.map((id) => [id, deletedIds.has(id) ? 'deleted' : 'failed'])),
          );
        }

        return { deleted: deletedIds.size, failed: promoCodeIds.length - deletedIds.size };
      } catch (deleteError) {
        if (visibleIdsInBatch.length > 0) {
          onStatusChange(Object.fromEntries(visibleIdsInBatch.map((id) => [id, 'failed' as const])));
        }
        throw deleteError;
      }
    },
    [currentPagePromos, onStatusChange],
  );

  const previewDelete = useCallback(async () => {
    if (isUnfilteredAllSearch || (deleteScope === 'search' && !loadedSearchPromoIds)) {
      return;
    }

    const promoCodeIds =
      deleteScope === 'page' ? currentPagePromos.map((promo) => promo.id_promo_code) : loadedSearchPromoIds;
    setPreviewing(true);
    setPreview(null);
    setError(null);

    try {
      const previewedPromoCodeIds = new Set<string>();
      const notFoundPromoCodeIds = new Set<string>();
      let batches = 0;

      for (const promoCodeIdBatch of chunkArray(promoCodeIds, PROMO_DELETE_BATCH_SIZE)) {
        batches += 1;
        const response = await bulkDeletePromoCodesByIds({ promoCodeIds: promoCodeIdBatch, dryRun: true });
        const previewResult = getPromoDeleteResult(response, promoCodeIdBatch, true);
        previewResult.promoCodeIds.forEach((id) => previewedPromoCodeIds.add(id));
        previewResult.notFoundPromoCodeIds.forEach((id) => notFoundPromoCodeIds.add(id));
      }

      setPreview({
        promoCodeIds: Array.from(previewedPromoCodeIds),
        notFoundPromoCodeIds: Array.from(notFoundPromoCodeIds),
        requested: promoCodeIds.length,
        batches,
      });
    } catch (previewError) {
      setError((previewError as Error).message || 'Unable to review promo code deletion.');
    } finally {
      setPreviewing(false);
    }
  }, [currentPagePromos, deleteScope, isUnfilteredAllSearch, loadedSearchPromoIds]);

  const submitDelete = useCallback(async () => {
    if (!preview || isUnfilteredAllSearch) {
      return;
    }
    setSubmitting(true);
    stopRequestedRef.current = false;
    setCompletion(null);
    setError(null);
    setProgress(null);

    try {
      const promoCodeIds = preview.promoCodeIds;
      let batch = 0;
      let processed = 0;
      let deleted = 0;
      let failed = 0;
      let totalRequestDuration = 0;
      const totalBatches = Math.ceil(promoCodeIds.length / PROMO_DELETE_BATCH_SIZE);

      for (const promoCodeIdBatch of chunkArray(promoCodeIds, PROMO_DELETE_BATCH_SIZE)) {
        if (stopRequestedRef.current) {
          break;
        }

        batch += 1;
        const requestStartedAt = Date.now();
        const averageRequestDuration = batch > 1 ? totalRequestDuration / (batch - 1) : undefined;
        setProgress({
          batch,
          deleted,
          estimatedFinishAt: averageRequestDuration
            ? requestStartedAt + averageRequestDuration * (totalBatches - batch + 1)
            : undefined,
          failed,
          isRequestInProgress: true,
          processed,
          total: promoCodeIds.length,
          totalBatches,
        });
        const result = await deletePromoIds(promoCodeIdBatch);
        totalRequestDuration += Date.now() - requestStartedAt;
        processed += promoCodeIdBatch.length;
        deleted += result.deleted;
        failed += result.failed;
        setProgress({
          batch,
          deleted,
          estimatedFinishAt:
            batch < totalBatches ? Date.now() + (totalRequestDuration / batch) * (totalBatches - batch) : undefined,
          failed,
          isRequestInProgress: false,
          processed,
          total: promoCodeIds.length,
          totalBatches,
          stopped: stopRequestedRef.current,
        });
      }
      setCompletion({
        deleted,
        failed,
        requested: promoCodeIds.length,
        stopped: stopRequestedRef.current,
      });
    } catch (deleteError) {
      setError((deleteError as Error).message || 'Unable to delete promo codes.');
    } finally {
      setSubmitting(false);
      setLoadedSearchPromoIds(null);
      setLoadedSearchFilters(null);
      setBatchLoadProgress(null);
      setPreview(null);
      void onComplete().catch((refreshError) => {
        setError(
          (refreshError as Error).message || 'Promo codes were deleted, but the results could not be refreshed.',
        );
      });
    }
  }, [deletePromoIds, isUnfilteredAllSearch, onComplete, preview]);

  const stopDelete = () => {
    stopRequestedRef.current = true;
  };

  const confirmDelete = async () => {
    await submitDelete();
    setDeleteConfirmationOpen(false);
  };

  const selectedCount = deleteScope === 'page' ? currentPagePromos.length : (loadedSearchPromoIds?.length ?? 0);

  return (
    <Accordion
      expanded={isBulkDeleteMode}
      disabled={isUnfilteredAllSearch}
      onChange={(_, expanded) => setBulkDeleteModeAndReset(expanded)}
      disableGutters
      sx={{
        border: 1,
        borderColor: 'error.light',
        borderRadius: 1,
        width: isBulkDeleteMode ? '100%' : 'fit-content',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DeleteOutline color="error" />
          <Stack>
            <Typography color="error.main" fontWeight="bold">
              Danger Zone
            </Typography>
            {isUnfilteredAllSearch && (
              <Typography variant="caption" color="text.secondary">
                Apply a search filter before enabling deletion
              </Typography>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ borderTop: 1, borderColor: 'error.light', bgcolor: 'background.paper' }}>
        <Stack spacing={2}>
          {isBulkDeleteMode && (
            <>
              <FormControl>
                <FormLabel>1. Scope</FormLabel>
                <RadioGroup row value={deleteScope} onChange={(_, value: DeleteScope) => changeDeleteScope(value)}>
                  <FormControlLabel
                    value="page"
                    control={<Radio />}
                    label={`Delete this page (${currentPagePromos.length})`}
                  />
                  <FormControlLabel
                    value="search"
                    control={<Radio />}
                    label={`Delete all matching results (${totalSearchResults})`}
                  />
                </RadioGroup>
              </FormControl>

              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="subtitle2">2. Review</Typography>
                <Alert severity="warning">
                  {deleteScope === 'page'
                    ? `Review the ${selectedCount} promo code${selectedCount === 1 ? '' : 's'} displayed on the current page before permanently deleting them.`
                    : loadedSearchPromoIds
                      ? `${loadedSearchPromoIds.length} matching promo ID${loadedSearchPromoIds.length === 1 ? '' : 's'} loaded. Review the deletion before permanently deleting them.`
                      : `Load the ${totalSearchResults} matching promo ID${totalSearchResults === 1 ? '' : 's'} when you are ready to review this deletion.`}
                </Alert>
                {loadedSearchFilters && (
                  <Box>
                    <Typography variant="body2">Applied search filters used to load these IDs:</Typography>
                    <Box component="ul" sx={{ mb: 0, mt: 0.5, pl: 3 }}>
                      {loadedSearchFilters.map((filter) => (
                        <Typography component="li" key={filter} variant="body2">
                          {filter}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
                {deleteScope === 'search' && !loadedSearchPromoIds && (
                  <Button
                    variant="outlined"
                    disabled={totalSearchResults === 0 || isSubmitting || isLoadingSearchBatch || isPreviewing}
                    onClick={() => void loadSearchPromoIds()}
                  >
                    {isLoadingSearchBatch ? 'Loading matching promo IDs...' : 'Load'}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  disabled={
                    (deleteScope === 'page' && selectedCount === 0) ||
                    (deleteScope === 'search' && !loadedSearchPromoIds) ||
                    isSubmitting ||
                    isLoadingSearchBatch ||
                    isPreviewing
                  }
                  onClick={() => void previewDelete()}
                >
                  {isLoadingSearchBatch || isPreviewing ? 'Reviewing deletion...' : 'Review deletion'}
                </Button>
                {isLoadingSearchBatch && (
                  <Button variant="text" onClick={cancelSearchBatchLoad}>
                    Cancel
                  </Button>
                )}
                {isLoadingSearchBatch && batchLoadProgress && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} aria-label="Loading matching promo IDs" />
                    <Typography variant="body2">
                      Loading {batchLoadProgress.loaded} of {batchLoadProgress.total} promo IDs (page{' '}
                      {batchLoadProgress.page}).
                    </Typography>
                  </Stack>
                )}
              </Stack>

              {preview && (
                <Alert severity={preview.promoCodeIds.length > 0 ? 'info' : 'warning'}>
                  Review complete: {preview.promoCodeIds.length} of {preview.requested} promo code
                  {preview.requested === 1 ? '' : 's'} will be deleted in {preview.batches} request
                  {preview.batches === 1 ? '' : 's'}.
                  {preview.notFoundPromoCodeIds.length > 0
                    ? ` ${preview.notFoundPromoCodeIds.length} could not be found and will not be deleted.`
                    : ''}
                </Alert>
              )}

              {progress && (
                <Alert severity={progress.failed > 0 ? 'warning' : 'success'}>
                  {progress.stopped ? 'Delete stopped. ' : ''}Request {progress.batch} of {progress.totalBatches}{' '}
                  {progress.isRequestInProgress ? 'in progress. ' : 'complete. '}Processed {progress.processed} of{' '}
                  {progress.total} promo codes: {progress.deleted} deleted
                  {progress.failed > 0 ? `, ${progress.failed} failed` : ''}.
                  {progress.estimatedFinishAt
                    ? ` Estimated finish: ${new Date(progress.estimatedFinishAt).toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}.`
                    : ''}
                </Alert>
              )}

              {isSubmitting && (
                <Alert severity="info">
                  Each delete request can take several minutes while related promo data is removed. Keep this page open
                  until the request progress completes.
                </Alert>
              )}

              {completion && !completion.stopped && (
                <Alert severity="success">
                  Deletion complete: the service confirmed {completion.deleted} of {completion.requested} promo code
                  {completion.requested === 1 ? '' : 's'} deleted.
                  {completion.failed > 0 ? ` ${completion.failed} could not be deleted.` : ''}
                </Alert>
              )}

              {completion?.stopped && (
                <Alert severity="warning">
                  Deletion stopped after {completion.deleted} of {completion.requested} promo code
                  {completion.requested === 1 ? '' : 's'} were deleted.
                </Alert>
              )}

              {error && <Alert severity="error">{error}</Alert>}

              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="subtitle2">3. Delete</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    color="error"
                    variant="contained"
                    startIcon={<DeleteOutline />}
                    disabled={!preview || preview.promoCodeIds.length === 0 || isSubmitting || isPreviewing}
                    onClick={() => {
                      setDeleteConfirmationOpen(true);
                    }}
                  >
                    {isSubmitting
                      ? 'Deleting promo codes...'
                      : `Delete ${preview?.promoCodeIds.length ?? 0} promo codes`}
                  </Button>
                  {isSubmitting && (
                    <Button variant="outlined" onClick={stopDelete}>
                      Stop delete
                    </Button>
                  )}
                </Stack>
              </Stack>

              {preview && preview.promoCodeIds.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Promo code IDs to delete ({preview.promoCodeIds.length})</Typography>
                  <Box
                    component="ul"
                    sx={{
                      display: 'grid',
                      gap: 0.5,
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      maxHeight: 240,
                      mb: 0,
                      mt: 1,
                      overflowY: 'auto',
                      pl: 3,
                    }}
                  >
                    {preview.promoCodeIds.map((promoCodeId) => (
                      <Typography component="li" key={promoCodeId} sx={{ fontFamily: 'monospace' }} variant="body2">
                        {promoCodeId}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              <Dialog
                onClose={
                  isSubmitting
                    ? undefined
                    : () => {
                        setDeleteConfirmationOpen(false);
                      }
                }
                open={isDeleteConfirmationOpen}
              >
                <DialogTitle>Confirm permanent promo code deletion</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Permanently delete {preview?.promoCodeIds.length ?? 0} promo code
                    {(preview?.promoCodeIds.length ?? 0) === 1 ? '' : 's'} from the{' '}
                    {deleteScope === 'page' ? 'current page' : 'matching search results'}? This will run in{' '}
                    {preview?.batches ?? 0} request{(preview?.batches ?? 0) === 1 ? '' : 's'} and cannot be undone.
                  </DialogContentText>
                  {isSubmitting && (
                    <DialogContentText sx={{ mt: 2 }}>
                      Deletion is in progress. Keep this dialog open until it completes.
                    </DialogContentText>
                  )}
                </DialogContent>
                <DialogActions>
                  {isSubmitting ? (
                    <Button onClick={stopDelete}>Stop delete</Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setDeleteConfirmationOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    color="error"
                    disabled={isSubmitting || !preview || preview.promoCodeIds.length === 0}
                    onClick={() => void confirmDelete()}
                    variant="contained"
                  >
                    {isSubmitting ? 'Deleting promo codes...' : 'Delete promo codes'}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default PromoBulkOperations;
